'use client'

import NextError from 'next/error'
import { useEffect } from 'react'
import { getGlobalSentry } from '../utils/init'

interface GlobalErrorBoundaryProps {
  error: Error & { digest?: string }
  fallback?: React.ComponentType<{ error: Error & { digest?: string } }>
}

/**
 * 全局错误边界组件
 * 处理应用级别的错误，自动上报到 Sentry
 */
export function GlobalErrorBoundary({
  error,
  fallback: FallbackComponent,
}: GlobalErrorBoundaryProps) {
  useEffect(() => {
    const sentry = getGlobalSentry()

    if (sentry) {
      try {
        // 使用全局实例上报错误，提供更多上下文信息
        sentry.captureException(error, {
          context: 'global_error',
          tags: {
            error_type: 'global_error',
            error_digest: error.digest || 'unknown',
            page: typeof window !== 'undefined' ? window.location.pathname : 'unknown',
          },
          extra: {
            error_stack: error.stack,
            error_message: error.message,
            error_name: error.name,
            user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
            timestamp: new Date().toISOString(),
          },
        })
      }
      catch (sentryError) {
        console.warn('Failed to report error to Sentry:', sentryError)
        // 降级到控制台日志
        console.error('Global Error (Sentry reporting failed):', error)
      }
    }
    else {
      // Sentry 不可用时的降级处理
      console.error('Global Error (Sentry not initialized):', error)
    }
  }, [error])

  // 如果提供了自定义 fallback 组件，使用它
  if (FallbackComponent) {
    return <FallbackComponent error={error} />
  }

  // 默认使用 Next.js 错误页面
  return (
    <html>
      <body>
        {/* `NextError` is the default Next.js error page component. Its type
        definition requires a `statusCode` prop. However, since the App Router
        does not exist status codes for errors, we simply pass 0 to render a
        generic error message. */}
        <NextError statusCode={0} />
      </body>
    </html>
  )
}
